/**
 * **边的路径** —— 我们自己画的连线（原来是 Vue Flow 的默认 `bezier` 边）。
 *
 * ## 形状：水平切线的三次贝塞尔
 *
 * ```text
 *   源 ●────╮                        ╭────● 目标
 *            ╰──────────╮   ╭───────╯
 *                       ╰───╯
 *   控制点 1 = (源.x + offset, 源.y)
 *   控制点 2 = (目标.x - offset, 目标.y)
 * ```
 *
 * 为什么是**水平**切线而不是直线段或曲线连接：端口在节点的左右两侧，
 * 水平出发/进入让"这条线从哪个端口出来"一眼看得出 —— 斜着连会把
 * 端口位置糊掉。偏移量随水平距离增长但有上下限（见 `offsetFor`），
 * 于是"挨在一起的两个节点"不会扭成一团、"隔很远的两端"也不会甩出去。
 *
 * ## 为什么还要**采样**（`polylineOf`）
 *
 * 命中测试（剪线、右键落在哪条线上）需要"点到线的距离"，而 SVG 的
 * `getPointAtLength` 是 **DOM API** —— 用它就意味着命中测试离不开浏览器。
 * 有了采样函数，"折线"可以**纯算**出来：
 *
 *   · 剪线（`cutGeometry`）可以直接拿折线求交，不必先量 DOM；
 *   · 套件里能直接断言"这条线的形状对不对"（DOM 版做不到）。
 *
 * ⚠️ 采样步数要**够密又不爆**：32 段在 1000px 的线上每段 ~30px，
 * 对"剪线要穿过它"这种判定绰绰有余（判定用的是线段相交 + 命中半径）。
 */
import type { Point } from '../../types/geometry'

export interface EdgePathOptions {
  /** 曲线的"鼓肚"比例：偏移 = 水平距离 × 这个值 */
  curvature?: number
  /** 偏移下限（太近的两端也要有点弧度，否则连线像直棍） */
  minOffset?: number
  /** 偏移上限（太远的两端不要甩出去） */
  maxOffset?: number
}

/** 默认值 —— 视觉上接近原来 Vue Flow 的默认贝塞尔 */
export const EDGE_CURVATURE = 0.25
export const EDGE_MIN_OFFSET = 16
export const EDGE_MAX_OFFSET = 120

/**
 * 控制点的水平偏移量。
 *
 * ⚠️ 按**水平**距离算，不看垂直距离：端口在左右两侧，
 * 竖直方向拉得再开也不该改变"从端口水平伸出去多远"。
 * 用总距离算的话，上下叠着的两个节点之间会鼓出一个大肚子。
 */
export function offsetFor(source: Point, target: Point, options: EdgePathOptions = {}): number {
  const curvature = options.curvature ?? EDGE_CURVATURE
  const min = options.minOffset ?? EDGE_MIN_OFFSET
  const max = options.maxOffset ?? EDGE_MAX_OFFSET
  const raw = Math.abs(target.x - source.x) * curvature
  if (!Number.isFinite(raw)) return min
  return Math.min(max, Math.max(min, raw))
}

/** 三次贝塞尔的四个控制点（源 / 控制1 / 控制2 / 目标） */
export function bezierControls(
  source: Point,
  target: Point,
  options: EdgePathOptions = {},
): [Point, Point, Point, Point] {
  const offset = offsetFor(source, target, options)
  return [
    source,
    { x: source.x + offset, y: source.y },
    { x: target.x - offset, y: target.y },
    target,
  ]
}

/** 路径的 `d` 属性（SVG） */
export function bezierPath(source: Point, target: Point, options: EdgePathOptions = {}): string {
  const [, c1, c2] = bezierControls(source, target, options)
  /* 两端重合时给一段极短的线 —— 空的 `d` 在 SVG 里是"不渲染"，会闪 */
  if (source.x === target.x && source.y === target.y) {
    return `M ${source.x} ${source.y} L ${source.x + 1} ${source.y}`
  }
  return `M ${source.x} ${source.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${target.x} ${target.y}`
}

/** 贝塞尔上 t 处的点（0..1） */
export function pointOnBezier(
  source: Point,
  target: Point,
  t: number,
  options: EdgePathOptions = {},
): Point {
  const [p0, p1, p2, p3] = bezierControls(source, target, options)
  const u = 1 - t
  const a = u * u * u
  const b = 3 * u * u * t
  const c = 3 * u * t * t
  const d = t * t * t
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  }
}

/**
 * 把这条边**采样成折线**（`segments + 1` 个点）。
 *
 * ⚠️ 命中测试与剪线都用它 —— 它们只需要"线大致在哪"，
 * 而折线让这件事**不依赖 DOM**（见文件头）。
 */
export function polylineOf(
  source: Point,
  target: Point,
  segments = 32,
  options: EdgePathOptions = {},
): Point[] {
  const count = Math.max(1, Math.floor(segments))
  const points: Point[] = []
  for (let i = 0; i <= count; i += 1) {
    points.push(pointOnBezier(source, target, i / count, options))
  }
  return points
}

/**
 * 一条边的**两个端点**（画布坐标）—— 由端口位置决定。
 *
 * ⚠️ 端口位置来自"量出来的偏移"（`handleGeometry`），不是猜的：
 * 折行、变参行、隐藏行都会让端口上下移动，猜出来的线会**接到空气上**。
 * 所以这里只做"节点位置 + 端口偏移"这一步加法。
 */
export function connectorPoints(
  sourceNode: { position: Point; sockets?: Record<string, Point> },
  sourceHandle: string | null | undefined,
  targetNode: { position: Point; sockets?: Record<string, Point> },
  targetHandle: string | null | undefined,
): { source: Point; target: Point } {
  const at = (
    node: { position: Point; sockets?: Record<string, Point> },
    handle: string | null | undefined,
  ): Point => {
    const offset = handle ? node.sockets?.[handle] : undefined
    return {
      x: node.position.x + (offset?.x ?? 0),
      y: node.position.y + (offset?.y ?? 0),
    }
  }
  return {
    source: at(sourceNode, sourceHandle),
    target: at(targetNode, targetHandle),
  }
}
